/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "@xstate/react";
import { X, Trash2 } from "lucide-react";
import { useSpring, animated } from "@react-spring/web";
import type { TransferEntry } from "../model/useTransfersUpload";
import { RemoveConfirmationModal } from "./RemoveConfirmationModal";

export type SnapshotLite = {
  context: {
    file?: File;
    artwork?: { noBgUrl?: string; noBgNoTrimUrl?: string; originalImageUrl?: string };
    source?: string;
    artworkId?: number;
    isReviewed: boolean;
  };
  hasTag: (tag: string) => boolean;
  value: unknown;
  tags: Set<string>;
};

type TransferEntryCardProps = {
  entry: TransferEntry;
  isSelected: boolean;
  onSelect: () => void;
  onCancel: (id: string) => void;
  onRemove: (id: string) => void;
};

export function TransferEntryCard({
  entry,
  isSelected,
  onSelect,
  onCancel,
  onRemove
}: TransferEntryCardProps) {
  const snapshot = useSelector(entry.ref, s => s) as SnapshotLite;
  const isLoading = snapshot.hasTag("loading");

  const [localUrl, setLocalUrl] = useState<string>("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Unique gradient ID to avoid conflicts between multiple spinners
  const gradientId = useMemo(() => `gradient-${entry.id}`, [entry.id]);

  // Physics-based rotation with pendulum-like motion
  const { rotate, scale } = useSpring({
    from: { rotate: 0, scale: 0.8 },
    to: async next => {
      if (isLoading) {
        // Initial scale up
        await next({ scale: 1, config: { tension: 200, friction: 20 } });
        // Continuous rotation with physics
        while (isLoading) {
          // eslint-disable-next-line no-await-in-loop
          await next({
            rotate: 360,
            config: {
              duration: 2200,
              // Smooth continuous motion with subtle speed variation
              easing: t => {
                // Use sine wave for smooth acceleration/deceleration
                // Offset by PI to start slow, speed up in middle, slow at end
                const wave = Math.sin(t * Math.PI * 2 + Math.PI) * 0.08;
                // Ensure we never stop by keeping base progress linear
                return t + wave;
              }
            }
          });
          // eslint-disable-next-line no-await-in-loop
          await next({ rotate: 0, immediate: true });
        }
      } else {
        await next({ scale: 0.8, config: { tension: 300, friction: 30 } });
      }
    },
    loop: false
  });

  // Animated gradient position and opacity
  const { opacity, arcLength } = useSpring({
    from: { opacity: 0, arcLength: 0 },
    to: {
      opacity: isLoading ? 1 : 0,
      arcLength: isLoading ? 50 : 0 // 50% of circle circumference
    },
    config: {
      tension: isLoading ? 150 : 300,
      friction: isLoading ? 20 : 25
    }
  });

  // Dynamic stroke width based on rotation position
  const strokeWidth = rotate.to(r => {
    // Convert rotation to radians and adjust for starting position
    const angle = ((r - 90) * Math.PI) / 180;
    // Use sine to make it thicker at bottom (3-5px) and thinner at top (1.5-3px)
    const widthVariation = Math.sin(angle) * 1.5 + 3.5;
    return Math.max(1.5, Math.min(5, widthVariation));
  });

  useEffect(() => {
    const file = snapshot.context.file ?? entry.file;

    // For library artworks, we don't have a file
    if (!file) {
      // Check if we have artwork data instead
      const art = snapshot.context.artwork;
      if (art && (art.originalImageUrl || art.noBgUrl || art.noBgNoTrimUrl)) {
        // We have artwork URLs, don't set local URL
        setLocalUrl("");
      } else {
        setLocalUrl("");
      }
      return undefined;
    }

    // For uploaded files, create object URL
    const url = URL.createObjectURL(file);
    setLocalUrl(url);
    return () => URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot.context.file, entry.file, snapshot.context.artwork]);

  const imageUrl = useMemo(() => {
    if (localUrl) return localUrl;
    const art = snapshot.context.artwork;
    return art?.noBgUrl || art?.noBgNoTrimUrl || art?.originalImageUrl || "";
  }, [localUrl, snapshot.context.artwork]);

  return (
    <>
      <div
        className={`group relative h-[110px] w-[110px] cursor-pointer overflow-hidden rounded-lg border-2 ${
          isSelected ? "border-blue-600" : "border-gray-200"
        } bg-white shadow-sm transition-all hover:shadow-md`}
        onClick={onSelect}
      >
        <div className="relative h-full w-full bg-gray-50">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Artwork"
              className={`h-full w-full object-cover ${isLoading ? "opacity-70" : ""}`}
            />
          ) : (
            <div
              className={`flex h-full w-full items-center justify-center text-gray-400 ${isLoading ? "opacity-70" : ""}`}
            >
              No preview
            </div>
          )}

          {isLoading && (
            <animated.div
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
              style={{
                opacity,
                transform: scale.to(s => `scale(${s})`)
              }}
            >
              <animated.svg
                width="36"
                height="36"
                style={{
                  transform: rotate.to(r => `rotate(${r}deg)`)
                }}
              >
                {/* Background track - thin and subtle */}
                <circle
                  cx="18"
                  cy="18"
                  r="15"
                  fill="none"
                  stroke="rgb(229 231 235)"
                  strokeWidth="1.5"
                  opacity="0.6"
                />

                {/* Animated colored arc */}
                <animated.circle
                  cx="18"
                  cy="18"
                  r="15"
                  fill="none"
                  stroke={`url(#${gradientId})`}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={arcLength.to(len => `${len} ${100 - len}`)}
                  pathLength="100"
                  transform="rotate(-90 18 18)"
                  style={{
                    filter: "drop-shadow(0 1px 2px rgba(45, 110, 255, 0.3))"
                  }}
                />

                {/* Leading dot for visual weight */}
                <animated.circle
                  cx="18"
                  cy="3"
                  r={strokeWidth.to(w => w * 0.5)}
                  fill="rgb(168 85 247)"
                  style={{
                    filter: "drop-shadow(0 0 4px rgba(172, 127, 244, 0.6))"
                  }}
                />

                <defs>
                  <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgb(37 99 235)" stopOpacity="0.2" />
                    <stop offset="30%" stopColor="rgb(37 99 235)" />
                    <stop offset="60%" stopColor="rgb(99 102 241)" />
                    <stop offset="100%" stopColor="rgb(168 85 247)" />
                  </linearGradient>
                </defs>
              </animated.svg>
            </animated.div>
          )}

          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/40 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100" />

          <div className="absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100">
            {isLoading ? (
              <button
                type="button"
                aria-label="Cancel upload"
                onClick={() => onCancel(entry.id)}
                className="inline-flex size-9 items-center justify-center rounded-lg border-2 border-gray-300 bg-white text-gray-700 shadow"
                data-testid="cancel-upload-button"
              >
                <X className="size-4" />
              </button>
            ) : (
              <button
                type="button"
                aria-label="Remove artwork"
                onClick={() => setShowConfirmModal(true)}
                className="inline-flex size-9 items-center justify-center rounded-lg border-2 border-gray-300 bg-white text-red-600 shadow"
                data-testid="remove-artwork-button"
              >
                <Trash2 className="size-4" />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between px-2 py-1 text-xs text-gray-600" />
      </div>

      <RemoveConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          onRemove(entry.id);
          setShowConfirmModal(false);
        }}
      />
    </>
  );
}
